// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Http Methods
import { getRequest as get } from 'api/utils/HttpMethods';
import queryString from 'api/utils/queryStringBuilder';

// Interfaces
import { Admin, isAdmin } from 'interfaces';

// Utils
import {
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from 'api/utils/queries';

/**
 * Query keys for admin-related queries.
 */
export const adminQueryKeys = {
  all: ['admin'],
  list: () => [...adminQueryKeys.all, 'list'],
  get: (email: string) => [...adminQueryKeys.all, email],
  getAdminByCognitoID: (cognitoID: string) => [
    ...adminQueryKeys.all,
    cognitoID,
  ],
};

/**
 * Fetches a list of all admins.
 * @returns {Promise<Admin[]>} A promise that resolves to an array of Admin objects.
 */
const listAdmins = async (): Promise<Admin[]> =>
  await get<Admin[]>('QBilling', '/resource/admin/all');

/**
 * Fetches an admin by email.
 * @param {string} email - The email of the admin to fetch.
 * @returns {Promise<Admin | null>} A promise that resolves to an Admin object or null if not found.
 */
export const getAdmin = async (email: string): Promise<Admin | null> =>
  await get<Admin>('QBilling', `/resource/admin${queryString({ email })}`);

/**
 * Fetches an admin by Cognito ID.
 * @param {string} cognitoID - The Cognito ID of the admin to fetch.
 * @returns {Promise<Admin | null>} A promise that resolves to an Admin object or null if not found.
 */
const getAdminByCognitoID = async (
  cognitoID: string
): Promise<Admin | null> => {
  return await get<Admin>(
    'QBilling',
    `resource/admin/cognitoID${queryString({ cognitoID })}`
  );
};

/**
 * Creates a new admin.
 * @param {Admin} admin - The admin object to create.
 * @returns {Promise<Admin>} A promise that resolves to the created Admin object.
 */
const createAdmin = async (admin: Admin): Promise<Admin> => {
  return createEntity<Admin>({
    entity: admin,
    functionName: 'createAdmin',
    isEntity: isAdmin,
    path: '/resource/admin',
  });
};

/**
 * Updates an existing admin.
 * @param {Admin} admin - The admin object to update.
 * @returns {Promise<Admin>} A promise that resolves to the updated Admin object.
 */
const updateAdmin = async (admin: Admin): Promise<Admin> => {
  return updateEntity<Admin>({
    entity: admin,
    functionName: 'updateAdmin',
    isEntity: isAdmin,
    path: '/resource/admin',
  });
};

/**
 * @returns {UseQueryResult<Admin[]>} The query result containing an array of Admin objects.
 */
export const useListAdmins = (): UseQueryResult<Admin[]> => {
  return useListQuery<Admin>({
    queryKey: adminQueryKeys.list(),
    fetchFunction: listAdmins,
    functionName: 'listAdmins',
    isEntity: isAdmin,
    enabled: true,
  });
};

/**
 * @param {string} email - The email of the admin to fetch.
 * @returns {UseQueryResult<Admin>} The query result containing the Admin object.
 */
export const useGetAdmin = (email: string): UseQueryResult<Admin> => {
  return useGetQuery<Admin>({
    queryKey: adminQueryKeys.get(email),
    fetchFunction: getAdmin,
    functionName: 'getAdmin',
    params: email,
    enabled: !!email,
    isEntity: isAdmin,
  });
};

/**
 * @param {string} cognitoID - The Cognito ID of the admin to fetch.
 * @returns {UseQueryResult<Admin | null>} The query result containing the Admin object or null if not found.
 */
export const useGetAdminByCognitoID = (
  cognitoID: string
): UseQueryResult<Admin | null> => {
  return useGetQuery<Admin>({
    queryKey: adminQueryKeys.getAdminByCognitoID(cognitoID),
    fetchFunction: getAdminByCognitoID,
    functionName: 'getAdminByCognitoID',
    params: cognitoID,
    enabled: !!cognitoID,
    isEntity: isAdmin,
  });
};

/**
 * @returns {UseMutationResult<Admin>} The mutation result for creating an Admin.
 */
export const useCreateAdmin = (): UseMutationResult<Admin> => {
  return useMutationQuery<Admin>({
    mutationFn: createAdmin,
    successMessage: 'Admin created successfully',
    errorMessage: 'Error Creating Admin',
    queryKey: adminQueryKeys.all,
  });
};

/**
 * @returns {UseMutationResult<Admin>} The mutation result for updating an Admin.
 */
export const useUpdateAdmin = (): UseMutationResult<Admin> => {
  return useMutationQuery<Admin>({
    mutationFn: updateAdmin,
    successMessage: 'Admin updated successfully',
    errorMessage: 'Error Updating Admin',
    queryKey: adminQueryKeys.all,
  });
};
